<template>
  <v-container fluid class="main-container" data-testid="decoration-library-list-root">
    <div class="dl-page" data-testid="dl-list-page">
      <div class="dl-header">
        <div class="d-flex align-center">
            <h1 class="dl-header__title">Decoration Library</h1>
            <HelpIcon
                audience="owner"
                slug="decoration-library"
                heading="find-a-piece-of-artwork"
            />
        </div>
        <p class="dl-header__lede">
          Every piece of artwork this account owns, with the decorations
          set up from it and the products using them. Artwork is shared
          across stores &mdash; edits reach every product.
        </p>
      </div>

      <!--
        Toolbar (ADR 0062 Locked Decision 10): search + status filter
        chips + List/Grid mode toggle + Add artwork.
      -->
      <div class="dl-toolbar">
        <div class="dl-toolbar__row">
          <div class="dl-search">
            <span class="material-icons dl-search__icon">search</span>
            <input
              v-model="query"
              type="text"
              class="dl-search__input"
              placeholder="Search artwork, client, or file name"
              data-testid="dl-search"
            />
          </div>

          <div class="dl-filters" role="group" aria-label="Status filter">
            <button
              v-for="f in STATUS_FILTERS"
              :key="f"
              type="button"
              class="dl-filter-chip"
              :class="{ 'dl-filter-chip--active': filter === f }"
              :data-testid="`dl-filter-${f}`"
              @click="filter = f"
            >
              {{ f }}
            </button>
          </div>

          <div class="dl-spacer" />

          <div class="dl-mode-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              class="dl-mode-btn"
              :class="{ 'dl-mode-btn--active': viewMode === 'list' }"
              data-testid="dl-mode-list"
              aria-label="List view"
              @click="setViewMode('list')"
            >
              <span class="material-icons">view_list</span>
              List
            </button>
            <button
              type="button"
              class="dl-mode-btn"
              :class="{ 'dl-mode-btn--active': viewMode === 'grid' }"
              data-testid="dl-mode-grid"
              aria-label="Grid view"
              @click="setViewMode('grid')"
            >
              <span class="material-icons">grid_view</span>
              Grid
            </button>
          </div>

          <button
            type="button"
            class="dl-btn dl-btn--primary"
            data-testid="dl-add-btn"
            @click="addArtwork"
          >
            <span class="material-icons">add</span>
            Add artwork
          </button>
        </div>

        <div class="dl-result-count" data-testid="dl-result-count">
          Showing {{ filteredArtworks.length }} of {{ artworks.length }}
          artworks &middot; thumbnails are contain-fitted, never cropped
        </div>
      </div>

      <div class="dl-status" data-testid="dl-status">
        <span v-if="loadError" class="dl-status-error">{{ loadError }}</span>
      </div>

      <!--
        P5 PR-C: AddArtworkWizard modal lives at the Library entry point.
        The legacy 3-step wizard at /distributor/decoration-library/new
        stays mounted (still routed via the product editor's "Add
        placement" affordance) until PR-D ships AddPlacementWizard.
      -->
      <AddArtworkWizard
        v-model="addArtworkOpen"
        :repository-id="defaultRepositoryId"
        :distributor-id="defaultRepositoryDistributorId"
        @created="onArtworkCreated"
        @reused="openDetail"
      />

      <!--
        Post-upload snackbar: success copy only. The "Add a placement
        now" CTA was removed as a dead affordance -- PR-D wires the real
        placement flow through this snackbar when it lands (gap-analysis
        plan 5, section 5.9).
      -->
      <div
        v-if="lastCreated"
        class="dl-snackbar"
        role="status"
        data-testid="dl-snackbar"
      >
        <span class="dl-snackbar__msg" data-testid="dl-snackbar-msg">
          Artwork uploaded.
        </span>
        <button
          type="button"
          class="dl-snackbar__close"
          data-testid="dl-snackbar-close"
          aria-label="Dismiss"
          @click="lastCreated = null"
        >
          &times;
        </button>
      </div>

      <!-- Loading -->
      <div
        v-if="artworksPending"
        class="dl-loading"
        data-testid="dl-loading"
      >
        <v-progress-circular indeterminate color="primary" />
        <span class="dl-loading-text">Loading artworks...</span>
      </div>

      <!-- Empty (no artworks at all, or none match search/filter) -->
      <div
        v-else-if="artworks.length === 0"
        class="dl-empty"
        data-testid="dl-empty"
      >
        <p class="dl-empty-text">
          No artworks yet. Upload an artwork to get started.
        </p>
      </div>
      <div
        v-else-if="filteredArtworks.length === 0"
        class="dl-empty"
        data-testid="dl-empty"
      >
        <p class="dl-empty-text">No artwork matches that search.</p>
      </div>

      <!-- List mode: grouped by client -->
      <div
        v-else-if="viewMode === 'list'"
        class="dl-list-view"
        data-testid="dl-list-view"
      >
        <div
          v-for="group in clientGroups"
          :key="group.key"
          class="dl-client-group"
          :data-testid="`dl-client-group-${group.key}`"
        >
          <div class="dl-client-group__header">
            <span class="dl-client-group__name">{{ group.name }}</span>
            <span class="dl-client-group__count">
              {{ group.artworks.length }}
              artwork{{ group.artworks.length === 1 ? "" : "s" }}
            </span>
            <div class="dl-client-group__rule" />
          </div>

          <div class="dl-rows">
            <div
              v-for="artwork in group.artworks"
              :key="artwork.id"
              class="dl-row"
              :data-testid="`dl-row-${artwork.id}`"
              @click="openDetail(artwork)"
            >
              <div
                class="dl-tile"
                :data-testid="`dl-tile-${artwork.id}`"
                :data-backdrop="tileBackdropKey(artwork)"
                :style="tileStyle(artwork)"
              >
                <div
                  v-if="previewKind(artwork) === 'pending'"
                  class="dl-tile__pending"
                  :data-testid="`dl-thumb-pending-${artwork.id}`"
                  role="status"
                  aria-label="Generating preview"
                >
                  <v-progress-circular
                    indeterminate
                    size="18"
                    width="2"
                    color="primary"
                  />
                  <span class="dl-tile__pending-text">
                    Generating preview...
                  </span>
                </div>
                <div
                  v-else-if="previewKind(artwork) === 'failed'"
                  class="dl-tile__failed"
                  :data-testid="`dl-thumb-failed-${artwork.id}`"
                >
                  <span>{{ previewFailedMessage(artwork) }}</span>
                </div>
                <div
                  v-else-if="previewKind(artwork) === 'unsupported'"
                  class="dl-tile__no-preview"
                  :data-testid="`dl-thumb-no-preview-${artwork.id}`"
                  role="img"
                  :aria-label="`${previewMessage(artwork)}. ${previewHint(artwork)}`"
                  :title="previewHint(artwork)"
                >
                  <span data-testid="dl-no-preview-label">{{
                    previewMessage(artwork)
                  }}</span>
                  <span
                    class="dl-tile__no-preview-hint"
                    :data-testid="`dl-no-preview-hint-${artwork.id}`"
                  >{{ previewHint(artwork) }}</span>
                </div>
                <DecorationArtThumb
                  v-else-if="thumbnailSrc(artwork)"
                  :src="thumbnailSrc(artwork)"
                  :ink-hex="artwork.dominant_ink_hex"
                  :ink-mixed="artwork.dominant_ink_mixed === true"
                  :alt="thumbAlt(artwork)"
                  class="dl-tile__img"
                  :data-testid="`dl-thumb-${artwork.id}`"
                  @resolved="onTileBackdrop(artwork, $event)"
                />
                <div
                  v-else
                  class="dl-tile__placeholder"
                  :data-testid="`dl-thumb-placeholder-${artwork.id}`"
                >
                  {{ initialOf(artwork) }}
                </div>
              </div>

              <div class="dl-row__body">
                <div class="dl-row__title-line">
                  <span class="dl-row__name">{{ displayName(artwork) }}</span>
                  <span
                    class="dl-status-chip"
                    :class="statusChipClass(artwork.status)"
                    :data-testid="`dl-status-chip-${artwork.id}`"
                  >
                    {{ statusLabel(artwork.status) }}
                  </span>
                </div>
                <div class="dl-row__file-facts">
                  {{ fileFacts(artwork) }}
                </div>
                <ArtworkIdentifiers
                  :artwork-id="artwork.id"
                  :design-code="artwork.design_code"
                  :decorator-code="artwork.decorator_artwork_code"
                  :colorway-count="artwork.colorway_count"
                  :facts="factsOf(artwork)"
                />
                <div class="dl-row__decorations">
                  <div
                    v-for="deco in artwork.decorations"
                    :key="deco.id"
                    class="dl-decoration-line"
                    :data-testid="`dl-decoration-${artwork.id}-${deco.id}`"
                  >
                    <span
                      class="dl-method-chip"
                      :class="methodChipClass(deco.method)"
                    >
                      {{ methodLabel(deco.method) }}
                    </span>
                    <span
                      v-if="deco.physical_dimensions"
                      class="dl-decoration-line__dims"
                    >
                      {{ deco.physical_dimensions }}
                    </span>
                    <span v-if="specLabel(deco.spec)">
                      {{ specLabel(deco.spec) }}
                    </span>
                    <span
                      v-if="deco.placements && deco.placements.length"
                      class="dl-decoration-line__sep"
                      >&middot;</span
                    >
                    <span v-if="deco.placements && deco.placements.length">
                      {{ deco.placements.join(", ") }}
                    </span>
                    <!--
                      This size's approval, worst across the design's
                      clients (the design page's own rule, ADR 0067). No
                      pill when the design has no client: there is no
                      question to answer.
                    -->
                    <ApprovalStatePill
                      v-if="hasSizeApproval(deco)"
                      class="dl-decoration-line__approval"
                      :state="sizeApprovalState(deco)"
                      :data-testid="`dl-size-approval-${deco.id}`"
                    />
                  </div>
                  <div
                    v-if="!artwork.decorations || !artwork.decorations.length"
                    class="dl-decoration-line dl-decoration-line--none"
                    :data-testid="`dl-decorations-none-${artwork.id}`"
                  >
                    No decorations yet
                  </div>
                </div>
              </div>

              <div
                class="dl-row__usage"
                :data-testid="`dl-usage-${artwork.id}`"
              >
                <template v-if="usageOf(artwork).product_count === 0">
                  <span class="dl-row__usage-empty">Not used yet</span>
                </template>
                <template v-else>
                  <div class="dl-row__usage-count">
                    {{ usageOf(artwork).product_count }}
                  </div>
                  <div class="dl-row__usage-detail">
                    products &middot;
                    {{ usageOf(artwork).store_count }}
                    store{{ usageOf(artwork).store_count === 1 ? "" : "s" }}
                  </div>
                  <div
                    v-if="usageOf(artwork).live_store_count > 0"
                    class="dl-row__usage-live"
                  >
                    {{ usageOf(artwork).live_store_count }} live
                  </div>
                </template>
              </div>

              <span class="material-icons dl-row__chevron">chevron_right</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Grid mode: compact recognition cards -->
      <div v-else class="dl-grid-view" data-testid="dl-grid-view">
        <div
          v-for="artwork in filteredArtworks"
          :key="artwork.id"
          class="dl-card"
          :data-testid="`dl-card-${artwork.id}`"
          @click="openDetail(artwork)"
        >
          <div class="dl-card__preview">
            <div
              class="dl-tile dl-tile--grid"
              :data-testid="`dl-card-tile-${artwork.id}`"
              :data-backdrop="tileBackdropKey(artwork)"
              :style="tileStyle(artwork)"
            >
              <div
                v-if="previewKind(artwork) === 'pending'"
                class="dl-tile__pending"
                :data-testid="`dl-card-thumb-pending-${artwork.id}`"
              >
                <v-progress-circular
                  indeterminate
                  size="20"
                  width="2"
                  color="primary"
                />
              </div>
              <div
                v-else-if="previewKind(artwork) === 'failed'"
                class="dl-tile__failed"
                :data-testid="`dl-card-thumb-failed-${artwork.id}`"
              >
                <span>{{ previewFailedMessage(artwork) }}</span>
              </div>
              <div
                v-else-if="previewKind(artwork) === 'unsupported'"
                class="dl-tile__no-preview"
                :data-testid="`dl-card-thumb-no-preview-${artwork.id}`"
                role="img"
                :aria-label="`${previewMessage(artwork)}. ${previewHint(artwork)}`"
                :title="previewHint(artwork)"
              >
                <span>{{ previewMessage(artwork) }}</span>
              </div>
              <DecorationArtThumb
                v-else-if="thumbnailSrc(artwork)"
                :src="thumbnailSrc(artwork)"
                :ink-hex="artwork.dominant_ink_hex"
                :ink-mixed="artwork.dominant_ink_mixed === true"
                :alt="thumbAlt(artwork)"
                class="dl-tile__img"
                :data-testid="`dl-card-thumb-${artwork.id}`"
                @resolved="onTileBackdrop(artwork, $event)"
              />
              <div
                v-else
                class="dl-tile__placeholder"
                :data-testid="`dl-card-thumb-placeholder-${artwork.id}`"
              >
                {{ initialOf(artwork) }}
              </div>
            </div>
          </div>
          <div class="dl-card__body">
            <div class="dl-card__name">{{ displayName(artwork) }}</div>
            <div class="dl-card__client">
              {{ clientNameOf(artwork) }}
            </div>
            <ArtworkIdentifiers
              :artwork-id="artwork.id"
              :design-code="artwork.design_code"
              :decorator-code="artwork.decorator_artwork_code"
              :colorway-count="artwork.colorway_count"
              :facts="factsOf(artwork)"
            />
            <div class="dl-card__methods">
              <span
                v-for="deco in artwork.decorations"
                :key="deco.id"
                class="dl-method-chip dl-method-chip--sm"
                :class="methodChipClass(deco.method)"
              >
                {{ methodLabel(deco.method) }}
              </span>
            </div>
            <div class="dl-card__footer">
              <span
                class="dl-status-chip dl-status-chip--sm"
                :class="statusChipClass(artwork.status)"
                :data-testid="`dl-card-status-${artwork.id}`"
              >
                {{ statusLabel(artwork.status) }}
              </span>
              <span class="dl-card__product-count">
                {{
                  usageOf(artwork).product_count === 0
                    ? "Unused"
                    : `${usageOf(artwork).product_count} product${
                        usageOf(artwork).product_count === 1 ? "" : "s"
                      }`
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
/**
 * DecorationLibraryList.vue -- Decoration Library index (ADR 0062).
 *
 * Decoration Library redesign, Unit 3 (frontend index). Replaces the
 * five-column table ("Artwork . Name . Methods . Used by . Actions")
 * with contain-fitted tiles, a client-grouped List mode, a compact Grid
 * mode, and rows that spell out what each artwork is and what it is used
 * on. See:
 *   - planning/design-handoffs/handoff_2026-08-02/
 *     2026-08-02-decoration-library-detail/adr/
 *     0062-library-index-containment-list-grid.md
 *   - .../design/Locked Decisions.md (items 1-12)
 *   - .../design/dl-list.jsx (reference implementation)
 *
 * Data source: ``GET /api/artworks/library`` -- unchanged endpoint,
 * extended in U2 (#2368) with ``decorations[]``, ``client``, ``usage``,
 * ``status``, and the sampled ``dominant_ink_hex`` (plus the raw
 * ``dominant_ink_luminance``, not read here). Each thumbnail renders
 * through the shared DecorationArtThumb, which picks the backdrop from
 * ``dominant_ink_hex`` (server-sampled per ADR 0062 and its 2026-10-06
 * superseding note).
 *
 * View mode (List/Grid) is a per-user preference persisted via U2's
 * ``GET``/``PATCH /api/me/preferences`` (``decoration_library_view_
 * mode``). List is the default, both as the initial render (before the
 * preferences fetch resolves) and as the server-side default for an
 * account with no stored preference.
 *
 * Lost-functionality watch (ADR 0062, intentional removals -- do not
 * reinstate): the em-dash "no methods" placeholder, delete-on-row (moved
 * to the detail view's Manage section), and any ARTWORK-level approval
 * chip (ADR 0042/0062). Approval is shown only per size, on each list
 * decoration line (Jake 2026-10-27; ADR 0067 size grain), from the
 * endpoint's ``decorations[].worst_approval``.
 */
import axios from "@/axios";
import { useArtworksStore } from "@/stores/artworks";
import AddArtworkWizard from "@/components/decoration/AddArtworkWizard.vue";
import DecorationArtThumb from "@/components/decoration/DecorationArtThumb.vue";
import ArtworkIdentifiers from "@/pages/distributor/decoration-library/ArtworkIdentifiers.vue";
import ApprovalStatePill from "@/pages/distributor/decoration-library/detail/family/ApprovalStatePill.vue";
import { artworkFactItems } from "@/pages/distributor/decoration-library/artworkFacts";
import { approvalUiState } from "@/utils/decorationFamilySelectors";
import { ART_BACKDROP_CSS } from "@/utils/artBackdrop";
import { formatArtworkName } from "@/utils/formatArtworkName";
import { artworkDisplayName } from "@/utils/artworkDisplay";
import {
  artworkImageSrc,
  artworkPreviewDisplay,
  hasNonTerminalPreview,
} from "@/utils/artworkPreviewState";
import HelpIcon from "@/components/Help/HelpIcon.vue";

// Client lazy-render poll interval. The server kicks the render when the
// list loads (ensure_preview inside the library endpoint); the client polls
// the list until every non-terminal tile reaches a terminal state, then
// stops. 2.5s balances "the raster appears promptly" against "don't hammer
// the endpoint" (plan 5.3 -- every 2-3s).
const PREVIEW_POLL_INTERVAL_MS = 2500;

// Library/usage status axis (ADR 0042, extended ADR 0062/0063). Approval
// is a separate axis and is never folded into this chip; it shows per
// size on the decoration lines (ApprovalStatePill).
const STATUS_META = {
  draft: { label: "Draft", className: "dl-status-chip--draft" },
  ready: { label: "Ready", className: "dl-status-chip--ready" },
  "in-use": { label: "In use", className: "dl-status-chip--in-use" },
  archived: { label: "Archived", className: "dl-status-chip--archived" },
};

// Decoration-method chip palette (handoff colors: purple/blue/orange/pink).
const METHOD_CLASS_BY_SLUG = {
  embroidery: "dl-method-chip--emb",
  screen_print: "dl-method-chip--screen",
  dtg: "dl-method-chip--dtg",
  dtf: "dl-method-chip--dtg",
  heat_transfer: "dl-method-chip--heat",
  htv: "dl-method-chip--heat",
};

const STATUS_FILTERS = ["All", "In use", "Ready", "Draft", "Archived"];

// Shown when a row arrives with no status at all.
const UNKNOWN_STATUS_LABEL = "Unknown status";

export default {
  name: "DecorationLibraryList",

  components: {
    HelpIcon,
    AddArtworkWizard,
    ApprovalStatePill,
    ArtworkIdentifiers,
    DecorationArtThumb,
  },

  inject: {
    distributorLayoutCapabilities: {
      default: () => [],
    },
  },

  data() {
    return {
      store: useArtworksStore(),
      artworks: [],
      artworksPending: true,
      // artwork id -> backdrop key reported by its DecorationArtThumb, so
      // the tile frame can match it (see tileStyle).
      tileBackdrops: {},
      loadError: null,
      // List/Grid mode (ADR 0062 Locked Decision 5). Defaults to "list"
      // until the /api/me/preferences fetch resolves (or forever, if it
      // fails -- fail toward the documented default).
      viewMode: "list",
      // Toolbar state.
      query: "",
      filter: "All",
      STATUS_FILTERS,
      // P5 PR-C: modal-based AddArtworkWizard state.
      addArtworkOpen: false,
      // Resolved on mount via /api/artwork_repositories/mine; the
      // wizard needs it to enforce the repository scope on the SAFRS
      // POST. Distributors today have a single auto-seeded repo per
      // tenant; SuperAdmin (non-impersonating) sees all and we pick the
      // first one (matches the DecorationAddWizard fallback).
      defaultRepositoryId: null,
      defaultRepositoryDistributorId: null,
      // Tracks the most recently created artwork so the post-upload
      // snackbar can confirm the upload. Cleared on dismiss or on a
      // fresh upload.
      lastCreated: null,
      // PR-6: setInterval handle for the DST/EMB preview poll. Non-null
      // only while at least one tile is in a non-terminal state; cleared
      // when all tiles reach terminal (ready / failed_permanent) and on
      // unmount so the poll never outlives the component.
      previewPollTimer: null,
      // PR-4b: in-flight guard for pollPreviews. The fixed-interval timer
      // is decoupled from the async fetch, so a poll response slower than
      // the interval could overlap the next tick -- two concurrent GETs
      // whose out-of-order resolve could clobber `this.artworks` with a
      // stale list. This flag makes a tick a no-op while a poll fetch is
      // still awaiting, so at most one poll fetch is ever in flight.
      previewPollInFlight: false,
    };
  },

  computed: {
    capabilities() {
      const inj = this.distributorLayoutCapabilities;
      if (Array.isArray(inj)) return inj;
      if (inj && typeof inj === "object" && Array.isArray(inj.value)) {
        return inj.value;
      }
      return [];
    },

    filteredArtworks() {
      const q = this.query.trim().toLowerCase();
      return this.artworks.filter((a) => {
        const matchQ =
          !q ||
          [
            this.displayName(a),
            this.clientNameOf(a),
            a.filename,
            a.original_filename,
            // Back-office and decorator ids are searchable too.
            a.design_code,
            a.decorator_artwork_code,
          ].some((s) => (s || "").toLowerCase().includes(q));
        const matchF = this.matchesStatusFilter(a, this.filter);
        return matchQ && matchF;
      });
    },

    // List mode: group the filtered artworks by client. Server order
    // (newest-uploaded-first, per the library endpoint's ORDER BY) is
    // preserved within each group -- ADR 0062 Locked Decision 6 ("within
    // a group, rows are ordered by most recently uploaded").
    clientGroups() {
      const order = [];
      const byKey = new Map();
      for (const artwork of this.filteredArtworks) {
        const key = artwork.client ? String(artwork.client.id) : "unassigned";
        const name = artwork.client ? artwork.client.name : "Unassigned";
        if (!byKey.has(key)) {
          byKey.set(key, { key, name, artworks: [] });
          order.push(key);
        }
        byKey.get(key).artworks.push(artwork);
      }
      return order.map((key) => byKey.get(key));
    },
  },

  async mounted() {
    this.$store.state.title = "Decoration Library";
    this.$store.state.topTitle = false;
    await Promise.all([
      this.loadArtworks(),
      this.loadDefaultRepository(),
      this.loadViewModePreference(),
    ]);
  },

  unmounted() {
    this.$store.state.title = "";
    this.$store.state.topTitle = false;
    // PR-6: never let the preview poll outlive the component.
    this.stopPreviewPoll();
  },

  methods: {
    // ----- View mode (ADR 0062 Locked Decision 5) -------------------------

    async loadViewModePreference() {
      try {
        const resp = await axios.get("/api/me/preferences");
        const prefs = (resp && resp.data && resp.data.preferences) || {};
        if (prefs.decoration_library_view_mode === "grid") {
          this.viewMode = "grid";
        } else {
          this.viewMode = "list";
        }
      } catch (err) {
        // Fail toward the documented default (List) rather than block
        // the page on a preference-read failure.
        this.viewMode = "list";
      }
    },

    async setViewMode(mode) {
      if (mode !== "list" && mode !== "grid") return;
      const previous = this.viewMode;
      this.viewMode = mode;
      if (previous === mode) return;
      try {
        await axios.patch("/api/me/preferences", {
          decoration_library_view_mode: mode,
        });
      } catch (err) {
        // Non-fatal: the UI already reflects the chosen mode for this
        // session; a persistence failure just means the next load falls
        // back to the last successfully-stored preference.
      }
    },

    // ----- Status + method vocab -------------------------------------------

    // A status this build has no entry for is shown as the server sent it,
    // on a neutral chip, and is reachable from "All" only. It must never be
    // relabelled Draft: that would file it under the Draft chip and claim a
    // state the server did not report (design philosophy principle 5).
    statusLabel(status) {
      const meta = STATUS_META[status];
      if (meta) return meta.label;
      return status ? String(status) : UNKNOWN_STATUS_LABEL;
    },

    statusChipClass(status) {
      const meta = STATUS_META[status];
      return meta ? meta.className : "dl-status-chip--unknown";
    },

    // The filter compares status KEYS through STATUS_META, so only a known
    // status can match a specific chip.
    matchesStatusFilter(artwork, filter) {
      if (filter === "All") return true;
      const meta = STATUS_META[artwork.status];
      return Boolean(meta) && meta.label === filter;
    },

    // ----- Per-size approval (ADR 0067: approval lives at the size) ------

    hasSizeApproval(deco) {
      // null = the design has no client; absent = an older payload.
      return Boolean(deco) && deco.worst_approval != null;
    },

    sizeApprovalState(deco) {
      return approvalUiState(deco.worst_approval);
    },

    methodLabel(method) {
      return (method && method.display_name) || (method && method.slug) || "";
    },

    methodChipClass(method) {
      const slug = method && method.slug;
      return METHOD_CLASS_BY_SLUG[slug] || "dl-method-chip--default";
    },

    specLabel(spec) {
      if (!spec) return "";
      const parts = [];
      // Stitches are NOT repeated here: the artwork-level facts line
      // (artworkFacts.js) owns the stitch count, including the real count
      // of a delivered digitized file this spec cannot know.
      if (spec.color_count != null) {
        parts.push(
          `${spec.color_count} color${spec.color_count === 1 ? "" : "s"}`,
        );
      }
      if (spec.pms) parts.push(spec.pms);
      return parts.join(" · ");
    },

    // ----- Usage + client -----------------------------------------------

    usageOf(artwork) {
      return (
        (artwork && artwork.usage) || {
          product_count: 0,
          store_count: 0,
          live_store_count: 0,
        }
      );
    },

    clientNameOf(artwork) {
      return (artwork && artwork.client && artwork.client.name) || "Unassigned";
    },

    // ----- Tile / containment (ADR 0062 Locked Decisions 1-4) ------------
    //
    // The tile is contain-fitted (object-fit: contain, never crop/stretch)
    // with a fixed outer size (92px list / 148px grid). The backdrop is
    // decided by the shared DecorationArtThumb (sampled ink -> WCAG
    // contrast); the tile only MIRRORS that decision via its ``resolved``
    // event so the padding ring around the art matches the art's own
    // backdrop. Non-image states (pending / failed / placeholder) keep the
    // light backdrop.

    onTileBackdrop(artwork, key) {
      if (artwork) this.tileBackdrops[artwork.id] = key;
    },

    tileBackdropKey(artwork) {
      return (artwork && this.tileBackdrops[artwork.id]) || "light";
    },

    tileStyle(artwork) {
      return { background: ART_BACKDROP_CSS[this.tileBackdropKey(artwork)] };
    },

    // ----- Preview state (PR-6, unchanged behavior) -----------------------

    previewDisplayFor(artwork) {
      // Map the row to a display decision via the shared helper: the
      // "No preview for this file type" state (EPS, PDF-less .ai) first,
      // else the artwork_preview sidecar (PR-4). null preview (raster/SVG)
      // -> kind 'none'.
      return artworkPreviewDisplay(artwork);
    },

    previewKind(artwork) {
      return this.previewDisplayFor(artwork).kind;
    },

    previewFailedMessage(artwork) {
      return this.previewDisplayFor(artwork).message || "";
    },

    previewMessage(artwork) {
      return this.previewDisplayFor(artwork).message || "";
    },

    previewHint(artwork) {
      return this.previewDisplayFor(artwork).hint || "";
    },

    thumbnailSrc(artwork) {
      // The shared tile-image rule (utils/artworkPreviewState.js), the same
      // one the product editor's artwork picker and the store editor's
      // Decorations rows use: a READY DST/EMB preview renders its cached
      // stitch raster (preview_url); otherwise the server thumbnail, then
      // image_url -- never a raw EPS/AI/PDF or stitch file (a broken
      // image). Non-null preview in any other state is handled by the
      // pending/failed tiles in the template.
      return artworkImageSrc(artwork);
    },

    displayName(artwork) {
      // The editable name (#703), else the uploaded file's name, else a
      // stable placeholder so the cell never renders empty. The '#<guid>'
      // identity suffix is stripped by the shared formatter.
      return artworkDisplayName(artwork);
    },

    thumbAlt(artwork) {
      return formatArtworkName(artwork.filename) || "artwork thumbnail";
    },

    initialOf(artwork) {
      const name = this.displayName(artwork);
      const first = String(name || "").trim().charAt(0);
      return first ? first.toUpperCase() : "A";
    },

    factsOf(artwork) {
      const slugs = ((artwork && artwork.methods) || []).map((m) => m.slug);
      return artworkFactItems(artwork, slugs);
    },

    fileFacts(artwork) {
      // "file name . W x H px . format" (ADR 0062 Locked Decision 6).
      const parts = [formatArtworkName(artwork.filename)];
      if (artwork.px) parts.push(`${artwork.px} px`);
      if (artwork.format) parts.push(artwork.format);
      return parts.filter(Boolean).join(" · ");
    },

    // ----- Data loading ----------------------------------------------------

    // PR-4b (DRY): the single fetch + parse against the library endpoint,
    // shared by loadArtworks (initial + explicit reloads) and pollPreviews
    // (silent poll re-fetch). Returns the parsed artworks array, or NULL
    // when the 200 body is malformed (artworks is not an array) -- the
    // "no usable data" sentinel. Throws on a network/HTTP failure. Each
    // caller applies its own policy to the null sentinel: loadArtworks
    // coerces null -> [] (a malformed initial load shows the empty state),
    // pollPreviews preserves the current list on null (a transient
    // malformed poll must not blow away the tiles -- the next tick retries).
    async _fetchLibraryArtworks() {
      const resp = await axios.get("/api/artworks/library");
      const body = (resp && resp.data) || {};
      return Array.isArray(body.artworks) ? body.artworks : null;
    },

    async loadArtworks() {
      this.artworksPending = true;
      this.loadError = null;
      try {
        // Malformed 200 -> null -> []; the empty state is the honest
        // render for an initial load with no usable artworks payload.
        this.artworks = (await this._fetchLibraryArtworks()) || [];
      } catch (err) {
        // Surface a load error inline. The Pinia/Vuex error bus is not
        // available in every mount context (the list is sometimes
        // mounted inside the SuperAdmin shell); keeping it local makes
        // the failure visible without requiring a store wire-up.
        this.artworks = [];
        this.loadError = "Could not load artworks";
      } finally {
        this.artworksPending = false;
      }
      // PR-6: (re)evaluate the lazy-render poll after every load. If any
      // tile is still non-terminal, ensure the poll is running; once all
      // tiles are terminal (or none has a preview), the poll stops. Loading
      // the list is itself the server-side render trigger (ensure_preview),
      // so the client just watches for the transition to a terminal state.
      this.syncPreviewPoll();
    },

    // ----- DST/EMB lazy-render poll (PR-6) --------------------------------

    syncPreviewPoll() {
      // Start the poll if there is anything non-terminal to watch; stop it
      // otherwise. Idempotent -- safe to call after every load.
      if (hasNonTerminalPreview(this.artworks)) {
        this.startPreviewPoll();
      } else {
        this.stopPreviewPoll();
      }
    },

    startPreviewPoll() {
      if (this.previewPollTimer != null) return; // already polling
      this.previewPollTimer = setInterval(() => {
        this.pollPreviews();
      }, PREVIEW_POLL_INTERVAL_MS);
    },

    stopPreviewPoll() {
      if (this.previewPollTimer != null) {
        clearInterval(this.previewPollTimer);
        this.previewPollTimer = null;
      }
    },

    async pollPreviews() {
      // A silent re-fetch (no spinner, no error banner takeover) that
      // refreshes the preview states in place. It reuses the library
      // endpoint -- loading it re-fires the server-side ensure_preview and
      // returns the latest sidecar states. The non-terminal check then
      // decides whether to keep polling.
      //
      // PR-4b in-flight guard: skip this tick if a prior poll fetch is
      // still awaiting. The fixed interval can outpace a slow response;
      // without this guard two concurrent GETs could race and an
      // out-of-order resolve could overwrite `this.artworks` with a stale
      // list. At most one poll fetch is in flight at a time.
      if (this.previewPollInFlight) return;
      this.previewPollInFlight = true;
      let latest;
      try {
        latest = await this._fetchLibraryArtworks();
      } catch (err) {
        // A transient poll failure must not blow away the list or stop the
        // poll -- the next tick retries. Leave the current tiles in place.
        return;
      } finally {
        this.previewPollInFlight = false;
      }
      // null sentinel = malformed 200 body -> preserve the current tiles
      // (a transient malformed poll must not wipe the list; the next tick
      // retries). A real array (including an empty one, e.g. all artworks
      // deleted) is applied.
      if (latest) this.artworks = latest;
      // Re-evaluate: stop once everything is terminal.
      this.syncPreviewPoll();
    },

    // ----- Add-artwork wizard ---------------------------------------------

    addArtwork() {
      // P5 PR-C: replaces the legacy route push to the 3-step wizard.
      // The legacy wizard at /distributor/decoration-library/new stays
      // mounted (still used by the product editor's "+ Add placement"
      // affordance) until PR-D ships AddPlacementWizard.
      this.addArtworkOpen = true;
    },

    async loadDefaultRepository() {
      try {
        const resp = await axios.get("/api/artwork_repositories/mine");
        const repos = (resp && resp.data && resp.data.repositories) || [];
        // Distributors today have a single auto-seeded repository;
        // SuperAdmin (non-impersonating) sees all and we pick the
        // first one ordered by id (matches /mine's ORDER BY).
        this.defaultRepositoryId = repos.length ? repos[0].id : null;
        this.defaultRepositoryDistributorId = repos.length
          ? repos[0].distributor_id ?? null
          : null;
      } catch (err) {
        // Non-fatal: the upload submit guards against a null
        // repositoryId and surfaces an inline error. The list still
        // renders without a repository (it's a browse surface).
        this.defaultRepositoryId = null;
        this.defaultRepositoryDistributorId = null;
      }
    },

    async onArtworkCreated(created) {
      // Surface the success snackbar, then refresh the list. The
      // library endpoint re-fetch is the path of least surprise -- it
      // picks up the full row shape (methods, decorations, usage,
      // status) for the new row in one call.
      this.lastCreated = created || null;
      await this.loadArtworks();
    },

    // ----- Navigation --------------------------------------------------

    openDetail(artwork) {
      // Named route ``library_artwork_detail`` mounted at
      // ``/decoration-library/artwork/:artwork_id``. ADR 0062 Locked
      // Decision 11: the row's primary action is OPEN -- delete is not
      // on the index at all (moved to the detail view's Manage section).
      if (!artwork || artwork.id == null) return;
      this.$router.push({
        name: "library_artwork_detail",
        params: { artwork_id: String(artwork.id) },
      });
    },
  },
};
</script>

<style scoped>
.dl-page {
  width: 100%;
  font-family: "Roboto", sans-serif;
  color: #212121;
}

.dl-header {
  margin-bottom: 18px;
}

.dl-header__title {
  font-family: "Poppins", sans-serif;
  font-size: 26px;
  font-weight: 500;
  color: #133752;
  letter-spacing: -0.3px;
  margin: 0 0 5px;
}

.dl-header__lede {
  font-size: 13.5px;
  color: var(--tf-fg-3);
  margin: 0;
  max-width: 660px;
}

/* ----- Toolbar ------------------------------------------------------- */

.dl-toolbar {
  margin-bottom: 18px;
}

.dl-toolbar__row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.dl-search {
  position: relative;
  flex: 1 1 280px;
  max-width: 380px;
}

.dl-search__icon {
  position: absolute;
  left: 12px;
  top: 9px;
  font-size: 18px;
  color: var(--tf-fg-4);
  pointer-events: none;
}

.dl-search__input {
  width: 100%;
  padding: 9px 12px 9px 38px;
  font-size: 13.5px;
  font-family: inherit;
  border: 1px solid #e0e0e0;
  border-radius: 9999px;
  background: #fff;
  color: #212121;
  outline: none;
  box-sizing: border-box;
}

.dl-filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.dl-filter-chip {
  padding: 5px 12px;
  font-size: 12.5px;
  border-radius: 9999px;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: var(--tf-fg-3);
  font-weight: 400;
}

.dl-filter-chip--active {
  border-color: #119ae9;
  background: #e3f2fd;
  color: #0b6fa4;
  font-weight: 500;
}

.dl-spacer {
  flex: 1;
}

.dl-mode-toggle {
  display: flex;
  background: #eceff1;
  border-radius: 9px;
  padding: 3px;
  gap: 2px;
}

.dl-mode-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 12.5px;
  border: none;
  background: transparent;
  color: var(--tf-fg-3);
  font-weight: 400;
  border-radius: 7px;
  cursor: pointer;
}

.dl-mode-btn .material-icons {
  font-size: 16px;
}

.dl-mode-btn--active {
  background: #fff;
  color: #212121;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.dl-btn {
  padding: 9px 18px;
  border-radius: 9999px;
  border: none;
  font-size: 13.5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.dl-btn--primary {
  background: #119ae9;
  color: #fff;
  font-weight: 500;
}

.dl-btn--primary .material-icons {
  font-size: 17px;
}

.dl-result-count {
  font-size: 12.5px;
  color: var(--tf-fg-4);
  margin-top: 12px;
}

.dl-status-error {
  color: #d32f2f;
  font-weight: 500;
  font-size: 13px;
}

/* ----- Loading / empty ------------------------------------------------ */

.dl-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
}

.dl-loading-text {
  color: rgba(0, 0, 0, 0.6);
}

.dl-empty {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  color: var(--tf-fg-4);
  font-size: 14px;
}

/* ----- Contain-fit tile (ADR 0062 Locked Decisions 1-4) --------------- */

.dl-tile {
  width: 92px;
  height: 92px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 11px; /* ~12% of 92px */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.dl-tile--grid {
  width: 148px;
  height: 148px;
  padding: 19px; /* ~13% of 148px */
  border: none;
  border-radius: 6px;
}

.dl-tile__img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.dl-tile__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.4);
  width: 100%;
  height: 100%;
}

.dl-tile__pending,
.dl-tile__failed {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
  width: 100%;
  height: 100%;
}

.dl-tile__pending-text {
  font-size: 9px;
  line-height: 1.15;
  color: rgba(0, 0, 0, 0.55);
}

.dl-tile__no-preview-hint {
  /* Visually hidden in the small tile (the full hint is the tooltip and
     part of the aria-label); kept in the DOM for assistive tech. */
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.dl-tile__no-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  height: 100%;
  padding: 4px;
  font-size: 9px;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.55);
}

.dl-tile__failed {
  font-size: 9px;
  line-height: 1.2;
  color: #8a1c1c;
}

/* ----- List mode: client groups + rows -------------------------------- */

.dl-client-group {
  margin-bottom: 18px;
}

.dl-client-group__header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
  padding: 0 2px;
}

.dl-client-group__name {
  font-size: 13px;
  font-weight: 600;
  color: #133752;
  letter-spacing: 0.2px;
}

.dl-client-group__count {
  font-size: 11.5px;
  color: var(--tf-fg-4);
}

.dl-client-group__rule {
  flex: 1;
  height: 1px;
  background: #e0e0e0;
}

.dl-rows {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
}

.dl-row {
  display: flex;
  gap: 18px;
  padding: 16px 18px;
  align-items: flex-start;
  cursor: pointer;
  border-bottom: 1px solid #e0e0e0;
}

.dl-rows .dl-row:last-child {
  border-bottom: none;
}

.dl-row:hover {
  background: #f7fafc;
}

.dl-row__body {
  flex: 1;
  min-width: 0;
}

.dl-row__title-line {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.dl-row__name {
  font-size: 16px;
  font-weight: 500;
  color: #212121;
  font-family: "Poppins", sans-serif;
}

.dl-row__file-facts {
  font-size: 12px;
  color: var(--tf-fg-4);
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  margin-bottom: 10px;
}

.dl-row__decorations {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dl-decoration-line {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 12.5px;
  color: var(--tf-fg-3);
}

.dl-decoration-line--none {
  color: var(--tf-fg-4);
  font-style: italic;
}

.dl-decoration-line__dims {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  color: #424242;
}

.dl-decoration-line__sep {
  color: var(--tf-fg-4);
}

.dl-row__usage {
  width: 160px;
  flex-shrink: 0;
  text-align: right;
}

.dl-row__usage-empty {
  font-size: 13px;
  color: var(--tf-fg-4);
}

.dl-row__usage-count {
  font-size: 20px;
  font-weight: 500;
  color: #212121;
  font-family: "Poppins", sans-serif;
  line-height: 1.2;
}

.dl-row__usage-detail {
  font-size: 12px;
  color: var(--tf-fg-4);
}

.dl-row__usage-live {
  font-size: 12px;
  color: #12805a;
  margin-top: 3px;
}

.dl-row__chevron {
  font-size: 20px;
  color: var(--tf-fg-4);
  margin-top: 4px;
}

.dl-row:hover .dl-row__chevron {
  color: #119ae9;
}

/* ----- Status chip (library/usage axis only -- ADR 0042) -------------- */

.dl-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 9999px;
  white-space: nowrap;
  line-height: 1.4;
}

.dl-status-chip--sm {
  font-size: 10.5px;
  padding: 2px 7px;
}

.dl-status-chip--draft {
  background: #eef0f3;
  color: var(--tf-fg-3);
}

.dl-status-chip--ready {
  background: #eaf4fc;
  color: #0b6fa4;
}

.dl-status-chip--in-use {
  background: #e6f7ed;
  color: #12805a;
}

.dl-status-chip--archived {
  background: #f5f5f5;
  color: var(--tf-fg-4);
}

.dl-status-chip--unknown {
  background: #fff;
  color: var(--tf-fg-3);
  border: 1px dashed #bdbdbd;
}

/* ----- Method chip ----------------------------------------------------- */

.dl-method-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 9999px;
  white-space: nowrap;
}

.dl-method-chip--sm {
  font-size: 10.5px;
  padding: 2px 7px;
}

.dl-method-chip--emb {
  background: #ede7f6;
  color: #5e35b1;
}

.dl-method-chip--screen {
  background: #e3f2fd;
  color: #0b6fa4;
}

.dl-method-chip--dtg {
  background: #fff3e0;
  color: #a15c00;
}

.dl-method-chip--heat {
  background: #fce4ec;
  color: #ad1457;
}

.dl-method-chip--default {
  background: #eceff1;
  color: var(--tf-fg-3);
}

/* ----- Grid mode -------------------------------------------------------- */

/* ADR 0062: the design reference's tracks (dl-list.jsx) are
   repeat(auto-fill, minmax(232px, 1fr)) -- a 232px card floor, so cards
   wrap into rows. The floor is clamped with min(232px, 100%) so a
   container narrower than one card gets one full-width column instead of
   overflowing. Do NOT lower the floor to 0: with a 0px track minimum
   auto-fit/auto-fill make one column per card, and every card lands in a
   single row that shrinks toward zero width as the library grows (the
   2026-10-27 "grid shows everything in one row" bug). */
.dl-grid-view {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(232px, 100%), 1fr));
  grid-auto-rows: 1fr;
  gap: 14px;
}

.dl-card {
  max-width: 260px;
}

.dl-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.dl-card:hover {
  border-color: #119ae9;
}

.dl-card__preview {
  padding: 14px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: center;
}

.dl-card__body {
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.dl-card__name {
  font-size: 13.5px;
  font-weight: 500;
  color: #212121;
  line-height: 1.35;
  font-family: "Poppins", sans-serif;
}

.dl-card__client {
  font-size: 11.5px;
  color: var(--tf-fg-4);
}

.dl-card__methods {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.dl-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 8px;
  margin-top: auto;
  border-top: 1px solid #e0e0e0;
}

.dl-card__product-count {
  font-size: 11.5px;
  color: var(--tf-fg-4);
}

/* ----- Post-upload snackbar (P5 PR-C) ---------------------------------- */

.dl-snackbar {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1100;
  background: #323232;
  color: #fff;
  border-radius: 4px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  font-size: 14px;
}

.dl-snackbar__close {
  background: none;
  border: none;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
}
</style>
